import { clamp, hash3, mulberry32, smoothstep } from '@/core/rng';
import type { FireSnapshot } from '@/sim/persistence/snapshot';
import { CHUNK_SIZE } from '@/world/chunk';
import { SCAR_CELL, type Overlay } from '@/world/overlay';
import type { EnvField } from '@/world/env';
import { BIOMES } from '@/world/types';
import type { Region } from '@/world/region';
import { weatherAt, seasonalDryness } from '@/world/weather';
import type { SereStore } from '@/world/sere';
import type { StrainField } from '@/world/strainField';
import type { StrainReads } from '@/world/strainReads';
import type { StrainTraits } from '@/world/strain';

/**
 * A deterministic wildfire on the SCAR_CELL lattice.
 *
 * The fire reads the immutable Region for fuel and slope and writes only to
 * the Overlay when a cell burns out — the baseline world is never touched.
 * Every stochastic decision is drawn from a hash of (cell coords, tick count),
 * so an identical ignition followed by identical ticks is an identical fire,
 * frame rate and call pattern notwithstanding. No wall clock in here: time is
 * always passed in.
 */

export interface BurningCell { x: number; z: number; intensity: number }

/**
 * A burn, as one thing rather than as ten thousand cells.
 *
 * `fire_burned` was declared in `events.ts` and emitted nowhere from the day
 * it was written, so a burn reached the durable record only through the sere
 * and never as a mark — and `ROADMAP.md` §27 measured what that cost: the
 * missing tenth kind of consequence, and with it six of the top hundred
 * levels (`test/level.ts` §9 puts the number on it). §16 already said what
 * shape it wants — *the fire-identity entity; three systems asking, build it
 * once* — and this is the minimal honest version.
 *
 * An episode is the span during which ANY cell is alight. `tools/burn.ts`
 * checked that this is not a lie: over twelve god-free years, **none** of the
 * 42 episodes burned two patches that never touched, so a merged episode is
 * always one fire. Counting clusters per *tick* says otherwise — one episode
 * reached 55 disconnected patches — but that is a single big fire with a
 * broken front, which is exactly what a fire looks like.
 */
export interface BurnEpisode {
  /** World-seconds of the first ignition and of the last cell going out. */
  from: number;
  to: number;
  /** Distinct cells that burned through to a scar. */
  cells: number;
  /** Centroid of what burned. */
  x: number;
  z: number;
  /** Half the wider side of the bounding box — the footprint, in metres. */
  radius: number;
  /** The `fire_started` ids that opened it, for the causal chain. */
  lit: string[];
}

/** The episode being accumulated, kept flat so it snapshots by spread. */
interface LiveBurn {
  from: number; cells: number;
  sx: number; sz: number;
  x0: number; z0: number; x1: number; z1: number;
  /** `fire_started` ids that opened or fed this burn. */
  lit: string[];
}

/** JSON turns ±Infinity into null; this turns it back. See `restore`. */
const fin = (v: number | null | undefined, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback;

interface Cell { gx: number; gz: number; fuel: number; ignitedAt: number; intensity: number }

const CELLS_PER_CHUNK = CHUNK_SIZE / SCAR_CELL;
const cellChunk = (gx: number, gz: number) =>
  `${Math.floor(gx / CELLS_PER_CHUNK)},${Math.floor(gz / CELLS_PER_CHUNK)}`;

const MAX_BURNING = 4000;   // safety cap: beyond it, the oldest cells are snuffed

export class FireSim {
  /** One spread step per world-minute. */
  readonly stepSeconds: number = 60;

  private readonly cells = new Map<string, Cell>();
  private last: number | null = null;
  private tickCount = 0;
  /** The burn in progress, if anything is alight. */
  private live: LiveBurn | null = null;
  /** Episodes that ended since the last drain. */
  private finished: BurnEpisode[] = [];

  private readonly traits: StrainTraits = { yield: 1, tolerance: 1, exposure: 1, spread: 1 };

  constructor(
    private readonly region: Region,
    private readonly overlay: Overlay,
    private readonly env?: EnvField,
    /** Succession's memory. Optional so a bare FireSim still runs alone. */
    private readonly sere?: SereStore,
    /**
     * The strains (2026-08-19, the wake), both halves optional together so a
     * bare FireSim still runs alone: `strains` reads what the ground's own
     * varieties make of its fuel — a flammable strain's country genuinely
     * burns more readily, through the SAME expression the habitat's fireRisk
     * multiplies — and `strainField` is where the fire WRITES: a burn cuts
     * whatever tilt stood on the cell, and then presses gently toward the
     * mixture's least-flammable member — natural selection, running whether
     * or not anyone is watching (TRAITS.md: "burn is the natural selection
     * event"; law 16).
     */
    private readonly strains?: StrainReads,
    private readonly strainField?: StrainField,
  ) {}

  /**
   * What a cell offers the fire: biome density dried by scant rain — then the
   * season, then today's sky, then the land's memory. Withered ground is
   * standing dead fuel; drought cures it further; rained-on ground will not
   * carry a flame — and "rained-on" now includes the rain actually falling,
   * so a fire that runs into the front that lit it stalls there. This is
   * where a Wither called two seasons ago becomes this season's catastrophe.
   */
  private fuelAt(gx: number, gz: number, now: number): number {
    const wx = (gx + 0.5) * SCAR_CELL, wz = (gz + 0.5) * SCAR_CELL;
    const site = this.region.siteAt(wx, wz);
    if (!site.land) return 0;
    if (site.flow > 2000) return 0;   // big rivers are firebreaks
    let fuel = BIOMES[site.biome].density * (1 - site.rain * 0.8);
    // burned ground is SPENT ground: without this a fire re-enters its own
    // scar the moment the cell leaves the burning set, and one ignition
    // walks the island for ever behind the rain — measured: 3,354 km² from
    // a single bolt, the island four times over. With a sere the refusal is
    // §60 in full: fuel returns by RECOVERY STAGE over decades (bare 0.05 →
    // grass 0.5 → shrub 0.9 → young 0.75 → climax 1), so how a place burns
    // next time depends on how long ago it burned last. Without one — a
    // bare FireSim in a test — the overlay's forty-day char stands in, and
    // the refusal must be total while fresh: a proportional discount
    // measurably lets fire creep back through its own ash.
    if (this.sere) {
      fuel *= this.sere.fuelFactorAt(wx, wz, now);
    } else {
      const scar = this.overlay.scarAt(wx, wz, now);
      if (scar) fuel *= smoothstep(0.35, 1, scar.age01);
    }
    // late summer cures the standing fuel; the wet half of the year damps it.
    // The same curve for every hand that lights anything, divine included —
    // a god who reads the season casts with it, which is law 17 as physics.
    fuel *= 0.60 + 0.55 * seasonalDryness(now);
    fuel *= 1 - weatherAt(this.region.seed, now, wx, wz).rain * 0.8;
    // the ground's own varieties: exposure is a MULTIPLIER on this exact
    // expression (strain.ts's axis contract), so a hardy strain's country is
    // ground the fire finds poorer and a flammable one's is ground it loves —
    // and the habitat's fireRisk multiplies the same number, so what the
    // settlers fear and what the fire does can never be two different fields
    if (this.strains) {
      this.strains.plantTraits(site.biome, wx, wz, now, this.traits);
      fuel *= this.traits.exposure;
    }
    const e = this.env?.at(wx, wz, now);
    if (e) {
      fuel += Math.max(0, -e.biomass) * 0.5;
      fuel *= 1 - clamp(e.moisture, 0, 1) * 0.85;
      fuel *= 1 + clamp(-e.moisture, 0, 1) * 0.4;
    }
    return clamp(fuel, 0, 1);
  }

  /** fuelAt for the world outside: probes and the lightning spark gate. */
  fuelAtWorld(wx: number, wz: number, now: number): number {
    return this.fuelAt(Math.floor(wx / SCAR_CELL), Math.floor(wz / SCAR_CELL), now);
  }

  private light(gx: number, gz: number, fuel: number, at: number): void {
    const key = `${gx},${gz}`;
    if (this.cells.has(key)) return;
    // the first cell to catch opens the episode, whether it was the sky's
    // bolt or a god's — the record does not know the difference, and law 10
    // means nothing that can be witnessed ever will
    this.beginBurn(at);
    this.cells.set(key, { gx, gz, fuel, ignitedAt: at, intensity: clamp(0.35 + fuel * 0.65, 0, 1) });
  }

  /** Returns false if the spot cannot burn (water, bare rock, no fuel). */
  ignite(wx: number, wz: number, now: number): boolean {
    const gx = Math.floor(wx / SCAR_CELL), gz = Math.floor(wz / SCAR_CELL);
    const fuel = this.fuelAt(gx, gz, now);
    if (fuel < 0.02) return false;
    if (this.last === null) this.last = now;
    this.light(gx, gz, fuel, now);
    return true;
  }

  /** Advance to world-time now; returns chunk keys ("cx,cz") needing invalidation. */
  tick(now: number, wind: { x: number; z: number }): string[] {
    const dirty = new Set<string>();
    if (this.last === null) { this.last = now; return []; }
    while (now - this.last >= this.stepSeconds) {
      // nothing burning: skip the dead time rather than stepping through it
      if (this.cells.size === 0) { this.last = now; break; }
      this.last += this.stepSeconds;
      this.tickCount++;
      this.step(this.last, wind, dirty);
      // the last cell went out: the burn is over and becomes one thing
      if (this.cells.size === 0) this.endBurn(this.last);
    }
    return [...dirty];
  }

  private step(time: number, wind: { x: number; z: number }, dirty: Set<string>): void {
    const wl = Math.hypot(wind.x, wind.z);
    const windy = wl > 1e-6;
    const wux = windy ? wind.x / wl : 0, wuz = windy ? wind.z / wl : 0;

    // Fires lie down at night. Humidity recovers after dark, and the run
    // that crosses a valley by afternoon barely holds its ground at 3 am —
    // without this the only thing that ever stopped a spread was the next
    // rain front, days out, and the first measured god-free year burned the
    // island seven times over (soak: ~770k scar cells standing). The damp is
    // multiplicative on spread only: burning cells still burn out, rain
    // still douses, and a dawn front picks the run back up.
    const tod = (((time % 86400) + 86400) % 86400) / 86400;
    const dayF = smoothstep(0.24, 0.34, tod) * (1 - smoothstep(0.76, 0.88, tod));
    const damp = 0.30 + 0.70 * dayF;

    // spread from a snapshot of the front, so this step's catches don't spread yet
    const front = [...this.cells.values()];
    for (const c of front) {
      // one stream per (cell, tick): the fire cannot be rerolled by call pattern
      const rnd = mulberry32(hash3(c.gx, c.gz, this.region.seed + this.tickCount * 0x9e37));
      const hHere = this.region.heightAt((c.gx + 0.5) * SCAR_CELL, (c.gz + 0.5) * SCAR_CELL);
      for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) {
        if (!dx && !dz) continue;
        // draw before any skip, so the stream stays aligned across neighbours
        const roll = rnd();
        const ngx = c.gx + dx, ngz = c.gz + dz;
        if (this.cells.has(`${ngx},${ngz}`)) continue;
        const fuel = this.fuelAt(ngx, ngz, time);
        // marginal fuel burns where it is lit but does not CARRY: 0.02 still
        // ignites (a divine strike can char wet ground), but percolation
        // through half-green country is what made every fire island-sized
        if (fuel < 0.10) continue;
        const inv = 1 / Math.hypot(dx, dz);
        // downwind spreads eagerly, upwind barely; uphill runs, downhill creeps
        const wf = windy ? 1.1 + 0.5 * (dx * inv * wux + dz * inv * wuz) : 1;
        const dh = this.region.heightAt((ngx + 0.5) * SCAR_CELL, (ngz + 0.5) * SCAR_CELL) - hHere;
        const sf = clamp(1 + dh * 0.02, 0.7, 1.4);
        // The catch curve is where fire size lives, and it is solved, not
        // felt: a burning cell makes ~n = burn/step ≈ 8 tries per neighbour,
        // so per-neighbour catch is q = 1−(1−p)^8, and the old base of 0.16
        // put every fuel above ~0.15 far past percolation — measured
        // (tools/firesize.ts): one summer bolt ran 520 km², half the island,
        // and even deep-wet fires reached 7 km². This line puts ordinary
        // summer fuel (~0.37) at q ≈ 0.2 (near-critical: fires that wander
        // and die), cured or withered fuel (~0.6) at q ≈ 0.55 (a real
        // blaze), and nothing under fuel 0.24 spreads at all — winter fire
        // exists only where a god withers the ground first, which is law 17
        // working exactly as written.
        const p = clamp(Math.max(0, fuel * 0.33 - 0.078)
                        * wf * sf * c.intensity * damp, 0, 0.85);
        if (roll < p) this.light(ngx, ngz, fuel, time);
      }
    }

    // burnout: a cell burns 240 + fuel*600 seconds, then becomes a scar —
    // unless rain arrives over it first, which douses it early. Divine rain
    // and the sky's own rain quench through the same test, so a storm that
    // overtakes the fire it lit ends it: fronts drift at ~3 m/s and a fire
    // walks nearer 0.3, which is why most storm-margin ignitions stay small.
    for (const [key, c] of this.cells) {
      const cwx = (c.gx + 0.5) * SCAR_CELL, cwz = (c.gz + 0.5) * SCAR_CELL;
      const doused =
        (this.env ? (this.env.at(cwx, cwz, time)?.moisture ?? 0) > 0.45 : false) ||
        weatherAt(this.region.seed, time, cwx, cwz).rain > 0.6;
      if (doused || time - c.ignitedAt >= 240 + c.fuel * 600) {
        this.cells.delete(key);
        this.scarOut(c, time, dirty);
      }
    }

    // safety cap: insertion order is ignition order, so the head is the oldest
    const over = this.cells.size - MAX_BURNING;
    if (over > 0) {
      let snuffed = 0;
      for (const [key, c] of this.cells) {
        if (snuffed++ >= over) break;
        this.cells.delete(key);
        this.scarOut(c, time, dirty);
      }
    }
  }

  private scarOut(c: Cell, time: number, dirty: Set<string>): void {
    const wx = (c.gx + 0.5) * SCAR_CELL, wz = (c.gz + 0.5) * SCAR_CELL;
    this.overlay.addScar(wx, wz, clamp(0.5 + c.fuel * 0.5, 0, 1), time);
    // the char above heals in forty days; this line is the decades
    this.sere?.recordBurn(wx, wz, time);
    // …and the fire is the selection event: whatever tilt stood here is cut
    // (a range built on standing growth loses most of itself, and a deep cut
    // un-settles what it hits — law 33), and then the ground leans a little
    // toward the least flammable thing that already grew here. One scar cell
    // is a nudge; a fire REGIME over the same valley converges, and can
    // genuinely settle a hardy strain with no god anywhere near it — which is
    // what makes the branch gate reachable by natural history alone.
    if (this.strainField) {
      for (const k of this.strainField.burn(wx, wz, SCAR_CELL * 0.75, time)) dirty.add(k);
      const host = this.strains?.dominantHost(this.region.siteAt(wx, wz).biome) ?? null;
      if (host) {
        for (const k of this.strainField.naturalSelect(host, wx, wz, SCAR_CELL * 0.5, time)) {
          dirty.add(k);
        }
      }
    }
    dirty.add(cellChunk(c.gx, c.gz));

    // …and this line is the memory. Counted where a cell actually finishes
    // burning rather than by scanning the alight set each tick: a cell scars
    // out exactly once, so the count is distinct cells without a Set.
    const b = this.live;
    if (b) {
      b.cells++;
      b.sx += wx; b.sz += wz;
      b.x0 = Math.min(b.x0, wx); b.x1 = Math.max(b.x1, wx);
      b.z0 = Math.min(b.z0, wz); b.z1 = Math.max(b.z1, wz);
    }
  }

  /** Open an episode the moment the first cell catches. */
  private beginBurn(time: number): void {
    if (this.live) return;
    this.live = { from: time, cells: 0, sx: 0, sz: 0,
                  x0: Infinity, z0: Infinity, x1: -Infinity, z1: -Infinity, lit: [] };
  }

  /**
   * Tell the live burn which `fire_started` opened it.
   *
   * This is what makes a god's fire *count*. The Chronicle credits a strand
   * through `parentEventIds`, and the chain runs strike → `fire_started` →
   * `fire_burned`; without this last link it broke at the burn and
   * `strike|burn+fire` — the tenth kind of consequence (`ROADMAP.md` §28) —
   * stayed unreachable even once burns were emitted.
   *
   * Capped, because a storm can light a great many bolts into one fire and
   * the ids ride inside the event. Eight is far past what any strand needs:
   * the Chronicle folds by strand, so repeats collapse anyway.
   */
  creditBurn(eventId: string): void {
    if (this.live && this.live.lit.length < 8) this.live.lit.push(eventId);
  }

  /** Close it when the last one goes out. */
  private endBurn(time: number): void {
    const b = this.live;
    this.live = null;
    if (!b || b.cells === 0) return;
    this.finished.push({
      from: b.from, to: time, cells: b.cells,
      x: b.sx / b.cells, z: b.sz / b.cells,
      radius: Math.max(SCAR_CELL / 2, Math.max(b.x1 - b.x0, b.z1 - b.z0) / 2),
      lit: [...b.lit],
    });
  }

  /**
   * Burns that ended since the last call. The Simulation drains these and
   * emits — `FireSim` deliberately holds no `EventBus`, exactly as it did
   * before, so the fire stays a physics model and the orchestrator stays the
   * only thing that speaks.
   */
  drainBurns(): BurnEpisode[] {
    const out = this.finished;
    this.finished = [];
    return out;
  }

  /**
   * The fire mid-flame, cells in ignition order (the safety cap snuffs the
   * head of the map, so insertion order is replay state). `last` and
   * `tickCount` ride along: the spread rng hashes tickCount, so losing
   * either resumes a different fire.
   */
  toSnapshot(): FireSnapshot {
    return {
      cells: [...this.cells.values()].map((c) => ({ ...c })),
      last: this.last,
      tickCount: this.tickCount,
      // The burn in progress, or a save mid-fire forgets what it was burning.
      // `lit` is copied rather than spread: `{...this.live}` shares the array,
      // so the "snapshot" went on collecting ignitions after it was taken and
      // a save/reload round-trip diverged from an uninterrupted run by one id.
      // A snapshot is a value; nothing in it may still be attached to the sim.
      ...(this.live ? { burn: { ...this.live, lit: [...this.live.lit] } } : {}),
    };
  }

  /** Resume a snapshotted fire in place. Shares nothing with the source. */
  restore(snap: FireSnapshot): void {
    this.cells.clear();
    for (const c of snap.cells) this.cells.set(`${c.gx},${c.gz}`, { ...c });
    this.last = snap.last;
    this.tickCount = snap.tickCount;
    // Optional, so a world saved before burns were remembered restores with
    // nothing in progress — which is the truth about it. No version bump.
    //
    // The `?? ±Infinity` is not defensive padding. An episode's bounding box
    // starts at ±Infinity and stays there until the first cell scars out, and
    // `JSON.stringify(Infinity)` is **null** — so a save taken in the first
    // couple of minutes of a fire came back with `x0 = null`, and `Math.min`
    // coerces that to 0. The burn's footprint would have silently stretched
    // to the world origin. Caught by round-tripping a one-tick-old fire,
    // which is why `test/burn.ts` saves at the earliest tick and not a
    // comfortable one.
    const b = snap.burn;
    this.live = b ? {
      from: b.from, cells: b.cells, sx: b.sx, sz: b.sz,
      x0: fin(b.x0, Infinity), z0: fin(b.z0, Infinity),
      x1: fin(b.x1, -Infinity), z1: fin(b.z1, -Infinity),
      lit: [...(b.lit ?? [])],
    } : null;
    this.finished = [];
  }

  get burning(): BurningCell[] {
    return [...this.cells.values()].map((c) => ({
      x: (c.gx + 0.5) * SCAR_CELL,
      z: (c.gz + 0.5) * SCAR_CELL,
      intensity: c.intensity,
    }));
  }

  get active(): boolean { return this.cells.size > 0; }
}
